<template>
  <div
    class="aha-input__wrapper"
    ref="inputContainer"
  >
    <input
      :type="type"
      :placeholder="placeholder"
      :class="[
        'aha-input',
        `aha-input-${size}`,
        {
          'rounded': rounded,
          'disabled': disabled,
          'aha-input-limit': maxLength,
          'hover:border-primary-90 focus:border-primary-90': highlightOnHover,
          error,
        },
        classes
      ]"
      :required="required"
      :autofocus="autofocus"
      :value="value"
      :min="min"
      :max="max"
      @input="handleInput"
      @keyup="handleKeyup"
      @keydown="handleKeydown"
      v-on="listeners"
      :maxlength="maxLength"
      ref="input"
      @focus="handleInputFocus"
      @blur="handleInputBlur"
      :disabled="disabled"
      :readonly="readonly"
      :autocomplete="autocomplete"
      :autocapitalize="autocapitalize"
      :autocorrect="autocorrect"
    >
    <span
      @click="focusInput()"
      class="aha-input--char-counter"
      v-if="maxLength && isCharacterCounterShowing && !hideCharacterCounter"
    >
      {{ characterCounter }}
    </span>
    <slot
      name="append"
    />
    <!-- invisible dom element to measure size of input for autoresize -->
    <div v-show="autoresize" class="aha-input__ghost-input" ref="ghostInput">
      {{ value }}
    </div>
  </div>
</template>

<script>
export default {
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      default: '',
      type: [String, Number],
    },
    type: {
      default: 'text',
      type: String,
    },
    placeholder: {
      default: '',
      type: String,
    },
    required: {
      default: false,
      type: Boolean,
    },
    autofocus: {
      default: false,
      type: Boolean,
    },
    size: {
      default: 'md',
      type: String,
    },
    maxLength: {
      default: null,
      type: Number,
    },
    classes: {
      type: String,
      default: '',
    },
    disabled: {
      default: false,
      type: Boolean,
    },
    readonly: {
      default: false,
      type: Boolean,
    },
    error: {
      type: Boolean,
      default: false,
    },
    focus: {
      type: Boolean,
      default: false,
    },
    autoresize: {
      type: Boolean,
      default: false,
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    autoresizeWhen: {
      type: String,
      default: 'keyup',
    },
    rounded: {
      type: Boolean,
      default: true,
    },
    highlightOnHover: {
      type: Boolean,
      default: true,
    },
    autoresizeOffset: {
      default: null,
      type: Number,
    },
    hideCharacterCounter: {
      default: false,
      type: Boolean,
    },
    min: {
      type: Number,
      default: null,
    },
    max: {
      type: Number,
      default: null,
    },
    autocorrect: {
      type: String,
      default: 'off',
    },
    autocapitalize: {
      type: String,
      default: 'none',
    },
  },
  data() {
    return {
      isCharacterCounterShowing: false,
    }
  },
  computed: {
    listeners() {
      const { input, keyup, ...listeners } = this.$listeners
      return listeners
    },
    characterCounter() {
      if (this.value && this.value.length > this.maxLength) {
        return 0
      }
      return this.value ? this.maxLength - this.value.length : this.maxLength
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.focus) {
        this.$refs.input.focus()
      }
    })
    if (this.autoresize) {
      this.setAutoresizeInputWidth()
    }
  },
  methods: {
    focusInput() {
      this.$refs.input.focus()
    },
    handleInputFocus() {
      this.isCharacterCounterShowing = true
    },
    handleInputBlur() {
      this.isCharacterCounterShowing = false
    },
    handleInput($event) {
      this.$emit('input', $event.target.value)
    },

    handleKeyup($event) {
      this.$emit('keyup', $event)
      this.handleAutoresizeInput('keyup')
    },

    handleKeydown($event) {
      this.$emit('keydown', $event)
      this.handleAutoresizeInput('keydown')
    },

    handleAutoresizeInput(type) {
      if (this.autoresize && this.autoresizeWhen === type) {
        this.setAutoresizeInputWidth()
      }
    },

    setAutoresizeInputWidth() {
      const input = this.$refs.ghostInput
      const width = input && input.getBoundingClientRect().width
      // autoresizeOffset to count input padding, etc
      const finalContainerWidth = width + this.autoresizeOffset

      const parentMaxWidthValue = window.getComputedStyle(this.$el.parentElement).maxWidth.replace('px', '')
      const parentMaxWidth = parentMaxWidthValue && parseInt(parentMaxWidthValue, 10)
      if (!Number.isNaN(parentMaxWidth) && finalContainerWidth >= parentMaxWidth) {
        this.$refs.inputContainer.style.width = `${parentMaxWidth}px`
        return
      }
      this.$refs.inputContainer.style.width = `${finalContainerWidth}px`
    },
  },
}
</script>

<style lang="scss" scoped>
.aha-input__wrapper {
  @apply relative;
}

.aha-input__ghost-input {
  @apply invisible absolute whitespace-nowrap;
  font-size: inherit;
  font-weight: inherit;
}

.aha-input {
  @apply appearance-none;
  @apply px-2 h-10 text-sm w-full border border-secondary-20;
  @apply focus:bg-primary-20 focus:outline-none;
  @apply placeholder-base-70;

  font-size: inherit;
  font-weight: inherit;
  &.error, &:hover.error {
    border: 1px solid #ff5050;
    border-color: #ff5050;
  }

}

.aha-input-limit {
  @apply pr-9;
}

.aha-input--char-counter {
  @apply absolute h-10 top-0 flex items-center right-2 text-secondary-40 text-sm font-normal;
}

.aha-input-group>.aha-input__wrapper>.aha-input {
  @apply rounded-l-none;
}

.aha-input-group:hover>.aha-input__wrapper>.aha-input {
  @apply border-primary-90 bg-primary-20;
}

.aha-input.aha-input-sm {
  @apply h-8 text-xs;
  + .aha-input--char-counter {
    @apply h-8;
  }
}
.aha-input.aha-input-md {
}
.aha-input.aha-input-lg {
}
</style>
